import { useCallback, useSyncExternalStore } from 'react';

/** localStorage key holding the id of the document the user is working on. */
export const ACTIVE_DOCUMENT_KEY = 'sdd.activeDocumentId';

const listeners = new Set<() => void>();

const notify = (): void => {
  for (const listener of listeners) listener();
};

/** The stored id, or `null` when there is none or storage is unavailable. */
export const readActiveDocumentId = (): string | null => {
  try {
    return window.localStorage.getItem(ACTIVE_DOCUMENT_KEY);
  } catch {
    return null;
  }
};

/** Persist (or clear with `null`) the active document id and wake every subscriber. */
export const writeActiveDocumentId = (id: string | null): void => {
  try {
    if (id === null) window.localStorage.removeItem(ACTIVE_DOCUMENT_KEY);
    else window.localStorage.setItem(ACTIVE_DOCUMENT_KEY, id);
  } catch {
    // Storage may be blocked (private mode, quota); the URL still carries the id.
  }
  notify();
};

const subscribe = (onStoreChange: () => void): (() => void) => {
  listeners.add(onStoreChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === ACTIVE_DOCUMENT_KEY) onStoreChange();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('storage', onStorage);
  };
};

/**
 * The document the browser remembers between reloads.
 *
 * The backend keeps a document for two idle hours; without this a reload would mean a new
 * upload. The id also lives in the URL of the review route, so this only decides where the
 * upload route redirects to on arrival.
 */
export const useActiveDocument = (): [string | null, (id: string | null) => void] => {
  const id = useSyncExternalStore(subscribe, readActiveDocumentId, () => null);
  const set = useCallback((next: string | null) => writeActiveDocumentId(next), []);
  return [id, set];
};
